import React, { useEffect, useState } from "react";
import { db, auth } from "../firebase";
import {
  collection,
  getDocs,
  updateDoc,
  setDoc,
  deleteDoc,
  getDoc,
  doc,
} from "firebase/firestore";
import {
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
} from "firebase/auth";
import { getUserRole } from "../utils/authUtils";
import Footer from "../components/Footer";
import "../styles/Admin.css";
import { logUserAction } from "../utils/logUtils";

interface UserData {
  uid: string;
  authUid?: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  password?: string;
}

// ✅ Création directe depuis le frontend (sans Cloud Function)
const createUserDirect = async (email: string, password: string): Promise<string> => {
  const admin = auth.currentUser;
  const adminEmail = admin?.email;
  const adminPassword = prompt(
    "🔐 Pour créer un utilisateur, veuillez confirmer votre mot de passe administrateur :"
  );

  if (!adminEmail || !adminPassword) {
    throw new Error("Email administrateur ou mot de passe manquant.");
  }

  try {
    // Déconnexion temporaire
    await signOut(auth);

    // Création du nouveau compte utilisateur
    const userCred = await createUserWithEmailAndPassword(auth, email, password);
    const newUid = userCred.user.uid;

    // Déconnexion puis reconnexion de l'admin
    await signOut(auth);
    await signInWithEmailAndPassword(auth, adminEmail, adminPassword);

    return newUid;
  } catch (error: any) {
    console.error("Erreur création utilisateur :", error);
    throw new Error(error.message || "Erreur lors de la création.");
  }
};

const Admin: React.FC = () => {
  const [users, setUsers] = useState<UserData[]>([]);
  const [authorized, setAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUser, setNewUser] = useState<UserData>({
    uid: "",
    firstName: "",
    lastName: "",
    email: "",
    role: "foodinnov",
    password: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);

  useEffect(() => {
    const checkAuthorizationAndFetchUsers = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser?.email) {
        setAuthorized(false);
        setLoading(false);
        return;
      }

      const email = currentUser.email.trim().toLowerCase();
      const role = await getUserRole(email);
      if (role === "admin") {
        setAuthorized(true);
        await fetchUsers();
      } else {
        setAuthorized(false);
      }

      setLoading(false);
    };

    checkAuthorizationAndFetchUsers();
  }, []);

  const fetchUsers = async () => {
    const snapshot = await getDocs(collection(db, "Users"));
    const usersList: UserData[] = [];

    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      usersList.push({
        uid: docSnap.id,
        firstName: data.firstName || "",
        lastName: data.lastName || "",
        email: docSnap.id,
        role: data.role || "non défini",
        authUid: data.authUid || "",
      });
    });

    setUsers(usersList);
  };

  const handleRoleChange = async (uid: string, newRole: string) => {
    try {
      // Récupérer les informations actuelles de l'utilisateur
      const userDoc = await getDoc(doc(db, "Users", uid));
      const currentData = userDoc.data();
      const oldRole = currentData?.role || "non défini";

      // Mise à jour du rôle dans Firestore
      await updateDoc(doc(db, "Users", uid), { role: newRole });

      // Enregistrer l'action dans les logs avec plus de détails
      const currentUser = auth.currentUser;
      if (currentUser?.email) {
        await logUserAction(
          currentUser.email,
          "Changement de type utilisateur",
          `Modification du type d'utilisateur de ${uid} : ${oldRole} → ${newRole}`
        );
      }

      // Mise à jour de l'interface utilisateur
      setUsers((prev) =>
        prev.map((u) => (u.uid === uid ? { ...u, role: newRole } : u))
      );

      alert(`✅ Type d'utilisateur mis à jour avec succès pour ${uid}`);
    } catch (err: any) {
      console.error("Erreur lors du changement de type d'utilisateur :", err);
      alert("❌ Erreur lors du changement de type d'utilisateur : " + (err.message || "inconnue"));
    }
  };

  const handleDeleteUser = async (uid: string) => {
    const confirmDelete = window.confirm("❗ Supprimer complètement cet utilisateur ?");
    if (!confirmDelete) return;

    try {
      await deleteDoc(doc(db, "Users", uid));
      setUsers((prev) => prev.filter((u) => u.uid !== uid));
      alert("✅ Utilisateur supprimé.");
    } catch (err: any) {
      console.error("Erreur suppression utilisateur :", err);
      alert("❌ Échec suppression : " + (err.message || "inconnue."));
    }
  };

  const handleAddOrUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = newUser.email.trim().toLowerCase();
    const password = newUser.password?.trim();

    if (!email || (!isEditing && !password)) {
      alert("Email et mot de passe requis.");
      return;
    }

    const userRef = doc(db, "Users", email);

    try {
      if (isEditing && editingUserId) {
        await updateDoc(userRef, {
          firstName: newUser.firstName,
          lastName: newUser.lastName,
          role: newUser.role,
        });

        setUsers((prev) =>
          prev.map((u) =>
            u.uid === editingUserId
              ? {
                  ...u,
                  firstName: newUser.firstName,
                  lastName: newUser.lastName,
                  role: newUser.role,
                }
              : u
          )
        );
      } else {
        const snapshot = await getDoc(userRef);
        if (snapshot.exists()) {
          alert("❌ Cet utilisateur existe déjà.");
          return;
        }

        // ✅ Création directe (sans function)
        const newAuthUid = await createUserDirect(email, password!);

        await setDoc(userRef, {
          firstName: newUser.firstName,
          lastName: newUser.lastName,
          role: newUser.role,
          authUid: newAuthUid,
        });

        setUsers((prev) => [
          ...prev,
          {
            uid: email,
            firstName: newUser.firstName,
            lastName: newUser.lastName,
            email,
            role: newUser.role,
            authUid: newAuthUid,
          },
        ]);
      }

      setNewUser({
        uid: "",
        firstName: "",
        lastName: "",
        email: "",
        role: "foodinnov",
        password: "",
      });
      setShowAddForm(false);
      setIsEditing(false);
      setEditingUserId(null);
    } catch (err: any) {
      console.error("Erreur création :", err);
      alert(err.message || "Erreur lors de la création.");
    }
  };

  if (loading) return <p>Chargement...</p>;
  if (!authorized) return <p>⛔ Accès refusé. Page réservée aux administrateurs.</p>;

  return (
    <div className="admin-container">
      <h1 style={{ textAlign: 'center' }}>Administration</h1>

      <h2>Gestion des rôles utilisateurs</h2>

      <button
        className="add-user-button"
        onClick={() => {
          setShowAddForm((v) => !v);
          setIsEditing(false);
          setNewUser({
            uid: "",
            firstName: "",
            lastName: "",
            email: "",
            role: "foodinnov",
            password: "",
          });
        }}
      >
        {showAddForm ? "Annuler" : "+ Ajouter un utilisateur"}
      </button>

      {showAddForm && (
        <form className="add-user-form" onSubmit={handleAddOrUpdateUser}>
          <input
            type="text"
            placeholder="Prénom"
            value={newUser.firstName}
            onChange={(e) =>
              setNewUser({ ...newUser, firstName: e.target.value })
            }
            required
          />
          <input
            type="text"
            placeholder="Nom"
            value={newUser.lastName}
            onChange={(e) =>
              setNewUser({ ...newUser, lastName: e.target.value })
            }
            required
          />
          <input
            type="email"
            placeholder="Email"
            value={newUser.email}
            onChange={(e) =>
              setNewUser({ ...newUser, email: e.target.value })
            }
            required
            disabled={isEditing}
          />
          {!isEditing && (
            <input
              type="password"
              placeholder="Mot de passe"
              value={newUser.password}
              onChange={(e) =>
                setNewUser({ ...newUser, password: e.target.value })
              }
              required
            />
          )}
          <select
            value={newUser.role}
            onChange={(e) =>
              setNewUser({ ...newUser, role: e.target.value })
            }
          >
            <option value="admin">Admin</option>
            <option value="foodinnov">Foodinnov</option>
            <option value="demo">Démo</option>
          </select>
          <button type="submit">{isEditing ? "Mettre à jour" : "Valider"}</button>
        </form>
      )}

      <table className="admin-table">
        <thead>
          <tr>
            <th>Nom</th>
            <th>Prénom</th>
            <th>Email</th>
            <th>Rôle</th>
            <th>Changer le rôle</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.uid}>
              <td>{user.lastName}</td>
              <td>{user.firstName}</td>
              <td>{user.email}</td>
              <td>{user.role}</td>
              <td>
                <select
                  value={user.role}
                  onChange={(e) => handleRoleChange(user.uid, e.target.value)}
                >
                  <option value="admin">Admin</option>
                  <option value="foodinnov">Foodinnov</option>
                  <option value="demo">Démo</option>
                </select>
              </td>
              <td>
                <button
                  className="edit-user-button"
                  onClick={() => {
                    setIsEditing(true);
                    setShowAddForm(true);
                    setEditingUserId(user.uid);
                    setNewUser({
                      uid: user.uid,
                      firstName: user.firstName,
                      lastName: user.lastName,
                      email: user.email,
                      role: user.role,
                      password: "",
                    });
                  }}
                >
                  ✏️ Modifier
                </button>
                <button
                  className="delete-user-button"
                  onClick={() => handleDeleteUser(user.uid)}
                >
                  🗑 Supprimer
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Footer />
    </div>
  );
};

export default Admin;
